import { computed, toValue, type MaybeRefOrGetter } from 'vue'
import { useRiskStore } from '@/stores/riskData'

function isEmptyValue(value: any): boolean {
  return (
    value == null ||
    value === '' ||
    (Array.isArray(value) && value.length === 0) ||
    (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0)
  )
}

// Try to find the display label for a stored value.
function labelForValue(field: any, value: any): string | null {
  // `items` may be a function when a field's options are membership/context
  // conditional (e.g. CetaHousehold how_is_property_used); resolve to the array.
  const items = typeof field?.items === 'function' ? field.items() : field?.items
  if (!items) return null
  if (Array.isArray(items)) {
    const match = items.find((it: any) => (it && typeof it === 'object' ? it.value === value : it === value))
    if (match != null) return typeof match === 'object' ? String(match.label ?? match.value) : String(match)
  } else if (typeof items === 'object' && value in items) {
    return String(items[value])
  }
  return null
}

// Convert an ISO date (YYYY-MM-DD) into UK format (DD/MM/YYYY).
function formatUkDate(value: any): string | null {
  if (typeof value !== 'string') return null
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value)
  return match ? `${match[3]}/${match[2]}/${match[1]}` : null
}

// Build the "Original value" message shown above a field.
function originalContent(field: any, value: any): string {
  if (isEmptyValue(value)) {
    return `<div class="alert alert-info data-original-values">No original data</div>`
  }
  let displayValue: string
  if (Array.isArray(value)) {
    displayValue = value.map((v) => labelForValue(field, v) ?? String(v)).join(', ')
  } else {
    const label = labelForValue(field, value)
    if (label != null) {
      displayValue = label
    } else if (field?.type === 'date') {
      displayValue = formatUkDate(value) ?? String(value)
    } else if (typeof value === 'boolean') {
      displayValue = value ? 'Yes' : 'No'
    } else if (typeof value === 'object') {
      displayValue = Object.values(value).join(', ')
    } else {
      displayValue = String(value)
    }
  }
  if (displayValue.trim() === '') {
    return `<div class="alert alert-info data-original-values">No original data</div>`
  }
  return `<div class="alert alert-info data-original-values">Original value: ${displayValue}</div>`
}

// Render a single stored value to its display string (label / date / bool / raw).
function displayFor(field: any, value: any): string {
  const label = labelForValue(field, value)
  if (label != null) return label
  if (field?.type === 'date') return formatUkDate(value) ?? String(value)
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  return String(value)
}

// Build the "Original value" box for a repeating list: one line per row, each row's input
// fields joined together (e.g. "Newsagent — 100%"). Empty rows/lists show "No original data".
function originalListContent(field: any, value: any): string {
  const elementSchema = field?.element?.schema ?? {}
  const rows = (Array.isArray(value) ? value : [])
    .map((row: any) =>
      Object.keys(elementSchema)
        .filter((k) => !NON_INPUT_TYPES.has(elementSchema[k]?.type))
        .map((k) => (isEmptyValue(row?.[k]) ? null : displayFor(elementSchema[k], row[k])))
        .filter(Boolean)
        .join(' — '))
    .filter((line: string) => line.trim() !== '')
  if (!rows.length) {
    return `<div class="alert alert-info data-original-values">No original data</div>`
  }
  return `<div class="alert alert-info data-original-values">Original value:${rows.map((r: string) => `<div>${r}</div>`).join('')}</div>`
}

// Field types that don't store user data, so they don't need an original-value box.
const NON_INPUT_TYPES = new Set(['static', 'button', 'hidden', 'form-nav', 'postcode-lookup'])

// Walk the schema and add an original-value box above each field.
// The path is used to look up the matching value in ShadowData.
function decorateWithOriginals(
  schema: Record<string, any>,
  originalValue: (path: string) => any,
  basePath = '',
): Record<string, any> {
  const updatedSchema: Record<string, any> = {}
  for (const key of Object.keys(schema)) {
    const field = schema[key]
    if (field?.type === 'object' && field.schema) {

      const path = basePath ? `${basePath}.${key}` : key
      updatedSchema[key] = {
        ...field,
        schema: decorateWithOriginals(field.schema, originalValue, path),
      }

    } else if (field?.schema) {

      updatedSchema[key] = {
        ...field,
        schema: decorateWithOriginals(field.schema, originalValue, basePath),
      }

    } else if (field?.type === 'list' && field.element?.schema) {

      // A repeating list gets one summary box for the whole list (the onMounted skip below doesn't
      // apply — a list's onMounted seeds rows, it doesn't hide the field).
      const path = basePath ? `${basePath}.${key}` : key
      const originalValueBox: Record<string, any> = { type: 'static', content: originalListContent(field, originalValue(path)) }
      if (field?.conditions) originalValueBox.conditions = field.conditions
      updatedSchema[`${key}_original`] = originalValueBox
      updatedSchema[key] = field

    } else {
      // Skip fields that don't hold data, or fields that hide themselves.
      // Otherwise we'd end up showing an original-value box on its own.
      const skipOriginalValue = NON_INPUT_TYPES.has(field?.type) || typeof field?.onMounted === 'function'
      if (!skipOriginalValue) {
        const path = basePath ? `${basePath}.${key}` : key
        const originalValueBox: Record<string, any> = { type: 'static', content: originalContent(field, originalValue(path)) }
        if (field?.conditions) originalValueBox.conditions = field.conditions
        updatedSchema[`${key}_original`] = originalValueBox
      }
      updatedSchema[key] = field
    }
  }
  return updatedSchema
}

/**
 * Adds original-value boxes when we're in review mode.
 * Outside review mode it just returns the schema unchanged.
 */
export function useShadowSchema(formSchema: MaybeRefOrGetter<Record<string, any>>) {
  const store = useRiskStore()

  const schemaForForm = computed(() => {
    const schema = toValue(formSchema)
    if (!store.isReviewMode || !store.riskData?.ShadowData) return schema
    return decorateWithOriginals(schema, store.originalValue)
  })

  // Incremented whenever ShadowData changes so Vueform redraws the original-value boxes.
  const shadowVersion = computed(() => store.shadowVersion)

  // Call this before moving to the next step so any new values are copied into ShadowData.
  function commitShadow(risk: Record<string, any> | null) {
    store.commitShadow(risk)
  }

  return { schemaForForm, shadowVersion, commitShadow }
}
